<script lang="ts">
  /**
   * The two exhibit kinds, as inline SVG.
   *
   * SVG rather than the canvas charts in `ScoreChart.svelte`: an exhibit has to
   * survive `window.print()` at 300dpi and recolour itself when `.brf-page`
   * redefines the theme tokens to the white-paper set. A canvas bitmap does
   * neither.
   *
   * Colour: one series, so no categorical palette — `--blue` carries the line and
   * `--orange` the return flow, and every coloured mark is directly labelled, so
   * nothing is identified by colour alone. Text wears text tokens throughout.
   * Both pairs pass the dataviz validator on white and on the light theme; on the
   * dark theme they sit above its lightness band, which is noted in UPDATES —
   * the app has one step per hue, and inventing a darker one off-system would be
   * worse than a slightly light mark.
   */
  import Provenance from './Provenance.svelte';
  import type { Exhibit, PartnershipData, RenderCtx, TimelineData } from './types';

  let { exhibit, ctx, number }: { exhibit: Exhibit; ctx: RenderCtx; number: number } = $props();

  const uid = $derived(`${ctx.idPrefix}-${exhibit.id}`);

  /* ── timeline ───────────────────────────────────────────────────────────── */
  const W = 760;
  const timeline = $derived(exhibit.kind === 'timeline' ? exhibit.data as TimelineData : null);
  const line = $derived.by(() => {
    if (!timeline) return null;
    const points = timeline.points;
    const max = timeline.max ?? 100;
    const hasEvents = !!timeline.events?.length;
    // Paper uses the same data and labels in a shallower chart. Shortening the
    // SVG viewBox (rather than setting a CSS height) preserves its full width.
    const paper = ctx.idPrefix !== 'brf-web';
    const pad = { t: hasEvents ? (paper ? 32 : 46) : 14, r: 18,
      b: paper ? 24 : 40, l: 36 };
    const h = hasEvents ? (paper ? 125 : 212) : (paper ? 120 : 170);
    const x = (i: number) => pad.l + (points.length > 1 ? (i * (W - pad.l - pad.r)) / (points.length - 1) : 0);
    const y = (v: number) => pad.t + (1 - v / max) * (h - pad.t - pad.b);
    const ticks = [0, 0.25, 0.5, 0.75, 1].map(f => ({ v: f * max, y: y(f * max) }));
    const coords = points.map((p, i) => ({ ...p, x: x(i), y: y(p.value) }));
    // Events are staggered on two rows so neighbouring labels cannot collide.
    const events = (timeline.events ?? []).map((event, order) => {
      const at = points.findIndex(p => p.date === event.date);
      if (at < 0) return null;
      const row = order % 2;
      return { ...event, x: x(at), y: y(points[at].value), labelY: (paper ? 11 : 14) + row * (paper ? 13 : 15) };
    }).filter((e): e is NonNullable<typeof e> => e !== null);
    return { h, pad, coords, ticks, events, baseline: y(0) };
  });

  /* ── partnership ────────────────────────────────────────────────────────── */
  const BOX_H = 58, GAP = 12, LEFT_H = 96, BOX_W = 200, LEFT_X = 8, RIGHT_X = W - BOX_W - 8;
  const partnership = $derived(exhibit.kind === 'partnership' ? exhibit.data as PartnershipData : null);
  const graph = $derived.by(() => {
    if (!partnership) return null;
    const them = partnership.nodes.filter(n => n.side === 'them');
    const us = partnership.nodes.filter(n => n.side === 'us');
    const stackH = us.length * BOX_H + Math.max(0, us.length - 1) * GAP;
    const bodyH = Math.max(stackH, LEFT_H);
    const returns = partnership.flows.filter(f => f.kind === 'return');
    const h = 10 + bodyH + (returns.length ? 56 : 10);
    const box = new Map<string, { x: number; y: number; w: number; h: number; cy: number }>();
    them.forEach((node, i) => {
      const y = 10 + (bodyH - LEFT_H) / 2 + i * (LEFT_H + GAP);
      box.set(node.id, { x: LEFT_X, y, w: BOX_W, h: LEFT_H, cy: y + LEFT_H / 2 });
    });
    us.forEach((node, i) => {
      const y = 10 + (bodyH - stackH) / 2 + i * (BOX_H + GAP);
      box.set(node.id, { x: RIGHT_X, y, w: BOX_W, h: BOX_H, cy: y + BOX_H / 2 });
    });
    const forward = partnership.flows.filter(f => f.kind !== 'return');
    const paths = forward.map((flow, i) => {
      const a = box.get(flow.from), b = box.get(flow.to);
      if (!a || !b) return null;
      // Fan the departures across the left box so three arrows never overlap.
      const spread = forward.length > 1 ? (i / (forward.length - 1) - 0.5) * (a.h - 40) : 0;
      const y0 = a.cy + spread;
      return {
        ...flow,
        d: `M${a.x + a.w} ${y0} C ${a.x + a.w + 150} ${y0}, ${b.x - 150} ${b.cy}, ${b.x - 6} ${b.cy}`,
        labelY: (y0 + b.cy) / 2 - 8,
      };
    }).filter((p): p is NonNullable<typeof p> => p !== null);
    const laneY = 10 + bodyH + 30;
    const back = returns.map(flow => {
      const a = box.get(flow.from), b = box.get(flow.to);
      if (!a || !b) return null;
      return {
        ...flow, laneY,
        d: `M${a.x + a.w / 2} ${a.y + a.h} L ${a.x + a.w / 2} ${laneY} L ${b.x + b.w / 2} ${laneY} L ${b.x + b.w / 2} ${b.y + b.h + 6}`,
      };
    }).filter((p): p is NonNullable<typeof p> => p !== null);
    return { h, box, them, us, paths, back, laneY };
  });

  const label = $derived(exhibit.label || `Exhibit ${number}`);
  const pointSummary = $derived(
    timeline ? timeline.points.map(p => `${p.date} ${p.value}`).join(', ') : '');
</script>

<figure class="brf-ex" class:brf-ex-timeline={!!timeline}>
  <figcaption>
    <span class="brf-ex-k"><b>{label}</b></span>
    <span class="brf-ex-t">{exhibit.title}</span>
  </figcaption>

  {#if line && timeline}
    <svg viewBox="0 0 {W} {line.h}" role="img"
         aria-label="{exhibit.title}. {timeline.unit ?? 'Series'}: {pointSummary}.">
      {#each line.ticks as tick, k (k)}
        <line x1={line.pad.l} x2={W - line.pad.r} y1={tick.y} y2={tick.y}
              stroke="var(--border)" stroke-width="1" />
        <text x={line.pad.l - 8} y={tick.y + 4} text-anchor="end" font-size="11"
              fill="var(--text-dim)">{tick.v}</text>
      {/each}
      <polygon fill="var(--blue)" opacity="0.08"
        points="{line.coords[0].x},{line.baseline} {line.coords.map(p => `${p.x},${p.y}`).join(' ')} {line.coords[line.coords.length - 1].x},{line.baseline}" />
      <polyline fill="none" stroke="var(--blue)" stroke-width="2" stroke-linejoin="round"
        points={line.coords.map(p => `${p.x},${p.y}`).join(' ')} />
      {#each line.coords as point, k (k)}
        <circle cx={point.x} cy={point.y} r="4" fill="var(--bg)" stroke="var(--blue)" stroke-width="2">
          <title>{point.date}: {point.value}</title>
        </circle>
        <text x={point.x} y={line.h - (ctx.idPrefix === 'brf-web' ? 22 : 9)} text-anchor="middle" font-size="11"
              fill="var(--text-dim)">{point.date}</text>
      {/each}
      {#each line.events as event, k (k)}
        <line x1={event.x} x2={event.x} y1={event.y - 7} y2={event.labelY + 5}
              stroke="var(--border-strong)" stroke-width="1" />
        <text x={event.x} y={event.labelY} text-anchor="middle" font-size="11"
              fill="var(--text-muted)">{event.label}</text>
      {/each}
      {#if timeline.callout}
        <text x={W - line.pad.r} y={line.coords[line.coords.length - 1].y + 22}
              text-anchor="end" font-size="11.5" font-weight="600"
              fill="var(--orange-ink)">{timeline.callout}</text>
      {/if}
    </svg>
  {:else if graph}
    <svg viewBox="0 0 {W} {graph.h}" role="img" aria-label={exhibit.title}>
      <defs>
        <marker id="{uid}-in" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
          <path d="M0 0L10 5L0 10z" fill="var(--blue)" />
        </marker>
        <marker id="{uid}-out" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
          <path d="M0 0L10 5L0 10z" fill="var(--orange)" />
        </marker>
      </defs>
      {#each graph.paths as flow, i (i)}
        <path d={flow.d} fill="none" stroke="var(--blue)" marker-end="url(#{uid}-in)"
              stroke-width={flow.kind === 'proposed' ? 1.5 : 2}
              stroke-dasharray={flow.kind === 'proposed' ? '5 4' : undefined} />
        {#if flow.label}
          <text x={W / 2} y={flow.labelY} text-anchor="middle" font-size="11.5"
                fill="var(--text-muted)">{flow.label}</text>
        {/if}
      {/each}
      {#each graph.back as flow, i (i)}
        <path d={flow.d} fill="none" stroke="var(--orange)" stroke-width="2.5"
              marker-end="url(#{uid}-out)" />
        {#if flow.label}
          <rect x={W / 2 - 190} y={flow.laneY - 9} width="380" height="18" fill="var(--bg)" />
          <text x={W / 2} y={flow.laneY + 4} text-anchor="middle" font-size="11.5"
                font-weight="600" fill="var(--orange-ink)">{flow.label}</text>
        {/if}
      {/each}
      {#each [...graph.them, ...graph.us] as node, k (k)}
        {@const b = graph.box.get(node.id)}
        {#if b}
          <rect x={b.x} y={b.y} width={b.w} height={b.h} rx="4" fill="var(--bg-surface)"
                stroke={node.proposed ? 'var(--border-strong)' : 'var(--text-muted)'}
                stroke-dasharray={node.proposed ? '4 3' : undefined} />
          <text
            x={b.x + b.w / 2}
            y={b.y + (node.meta?.length ? (node.side === 'them' ? 38 : 24) : b.h / 2 + 5)}
            text-anchor="middle" font-size={node.side === 'them' ? 18 : 13.5}
            font-weight="600" fill="var(--text)">{node.label}</text>
          {#each node.meta ?? [] as meta, m (m)}
            <text x={b.x + b.w / 2} y={b.y + (node.side === 'them' ? 60 : 44) + m * 18}
                  text-anchor="middle" font-size="11.5" fill="var(--text-muted)">{meta}</text>
          {/each}
        {/if}
      {/each}
    </svg>
  {/if}

  {#if exhibit.note}
    <p class="brf-ex-src">{exhibit.note.text}<Provenance provenance={exhibit.note.provenance}
      sourceIds={exhibit.note.source_ids} {ctx} /></p>
  {/if}
</figure>
